"use client";

import type { RowData } from "@tanstack/react-table";
import {
  SortableContext,
  horizontalListSortingStrategy,
} from "@dnd-kit/sortable";
import { Table } from "@/components/ui/table";
import { ACTIONS_COLUMN_WIDTH } from "./grid-metrics";
import { RecordGridHeadCell } from "./record-grid-head-cell";
import { RecordGridBody, RecordGridEmpty } from "./record-grid-body";
import { RecordGridMobileList } from "./record-grid-mobile-list";
import type { RecordGridProps } from "./record-grid-props";
import type { RecordGridModel } from "./use-record-grid-model";

interface RecordGridViewProps<TData extends RowData> {
  props: RecordGridProps<TData>;
  grid: RecordGridModel<TData>;
}

function RecordGridHeader<TData extends RowData>({
  props: { object, state },
  grid: { table, columnIds, columnWidths, fillerWidth, hasRowActions },
}: RecordGridViewProps<TData>) {
  return (
    <thead>
      {table.getHeaderGroups().map((headerGroup) => (
        <tr key={headerGroup.id}>
          <SortableContext
            items={columnIds}
            strategy={horizontalListSortingStrategy}
          >
            {headerGroup.headers.map((header) => (
              <RecordGridHeadCell
                key={header.id}
                header={header}
                object={object}
                sort={state.sort}
                onSortChange={state.setSort}
                reorderable={object.capabilities.columnEditing}
                width={columnWidths.get(header.column.id)}
              />
            ))}
          </SortableContext>
          {fillerWidth > 0 ? (
            <th
              aria-hidden="true"
              style={{ width: fillerWidth }}
              className="sticky top-0 z-20 border-b border-border bg-surface-2"
            />
          ) : null}
          {hasRowActions ? (
            <th
              aria-label="Row actions"
              style={{ width: ACTIONS_COLUMN_WIDTH }}
              className="sticky top-0 right-0 z-30 border-b border-border bg-surface-2"
            />
          ) : null}
        </tr>
      ))}
    </thead>
  );
}

export function RecordGridMobileView<TData extends RowData>({
  props,
  grid,
}: RecordGridViewProps<TData>) {
  return (
    <div className="md:hidden">
      <RecordGridMobileList
        rows={grid.rows}
        object={props.object}
        rowActions={props.rowActions}
        onRowClick={props.onRowClick}
        onRowMenu={grid.rowMenu.onRowMenu}
        renderMobileRow={props.renderMobileRow}
        renderExpandedRow={props.renderExpandedRow}
        isLoading={grid.isLoading}
        isStale={grid.isStale}
        emptyTitle={props.emptyTitle}
        emptyDescription={props.emptyDescription}
        emptyAction={props.emptyAction}
      />
    </div>
  );
}

export function RecordGridDesktopTable<TData extends RowData>({
  props,
  grid,
}: RecordGridViewProps<TData>) {
  const { hasRowActions } = grid;
  return (
    <>
      <Table
        noWrapper
        className="hidden w-max table-fixed border-separate border-spacing-0 md:table"
        style={{ width: grid.tableWidth }}
      >
        <RecordGridHeader props={props} grid={grid} />
        <RecordGridBody
          rows={grid.rows}
          object={props.object}
          columnSpan={grid.visibleColumnCount + (grid.fillerWidth > 0 ? 1 : 0) + (hasRowActions ? 1 : 0)}
          columnWidths={grid.columnWidths}
          columnOrderKey={grid.columnIds.join("|")}
          hasFiller={grid.fillerWidth > 0}
          isLoading={grid.isLoading}
          isStale={grid.isStale}
          rowActions={props.rowActions}
          onRowClick={props.onRowClick}
          onRowHover={props.onRowHover}
          onRowMenu={grid.rowMenu.onRowMenu}
          activeRowId={props.activeRowId}
          getRowClassName={props.getRowClassName}
          renderExpandedRow={props.renderExpandedRow}
        />
      </Table>
      {grid.rows.length === 0 && !grid.isLoading ? (
        <RecordGridEmpty
          title={props.emptyTitle}
          description={props.emptyDescription}
          action={props.emptyAction}
        />
      ) : null}
    </>
  );
}
